import React, { useEffect, useMemo, useRef, useState } from "react";
import { components } from "react-select";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../hooks/formatDateTime";
import { useDispatch, useSelector } from "react-redux";
import TranslateText from "./TranslateText";
import AddStrikeModal from "./Modals/User/AddStrikeModal";
import CloseStrike from "./Modals/User/CloseStrike";
import AddRestrictionModal from "./Modals/User/AddRestrictionModal";
import RestrictionActionModal from "./Modals/User/RestrictionActionModal";
import { apply_strike, close_strike, get_Strike_Category_item, upload_image, get_strike_data, apply_restriction, change_restriction_status, get_restriction_data } from "../reduxData/user/userAction";
import { 
  AlertTriangle,
  Ban,
  ChevronDown,
  FileImage,
  Gavel,
  MessageSquareWarning,
  Package,
  RotateCcw,
  Search,
  ShieldAlert,
  Truck,
  X,
} from "lucide-react";

const { REACT_APP_IMAGE_URL } = process.env;

const initialStrikeForm = {
  category: "",
  severity: "",
  reason: "",
  description: "",
  reference_type: "",
  reference_id: "",
  reference_data: null,
  evidence: [],
};

const initialRestrictionForm = {
  restriction_type: "",
  reason: "",
  description: "",
  permanent: false,
  expires_at: "",
};

const RESTRICTION_TYPES = [
  { value: "PAYOUT_HOLD", label: "Payout Held", description: "Hold seller payouts while the account is reviewed." },
  { value: "FINAL_SALE_BANNED", label: "Final Sale Banned", description: "Block final-sale participation for this account." },
  { value: "UPLOAD_RESTRICTED", label: "Upload Restricted", description: "Restrict product upload access." },
  { value: "SELLING_RESTRICTED", label: "Selling Restricted", description: "Restrict selling activity." },
  { value: "ACCOUNT_UNDER_REVIEW", label: "Account Under Review", description: "Place the account into manual review." },
  { value: "ACCOUNT_SUSPENDED", label: "Account Suspended", description: "Suspend account access until the restriction is removed." },
];

const SELLER_STRIKE_CATEGORIES = [
  {
    value: "SELLER_MISLEADING_PRODUCT",
    label: "Misleading Product",
    description: "Product information or representation is inaccurate.",
  },
  {
    value: "SELLER_LATE_SHIPMENT",
    label: "Late Shipment",
    description: "Seller failed to ship within the required deadline.",
  },
  {
    value: "SELLER_DOES_NOT_SHIP",
    label: "Seller Does Not Ship",
    description: "Seller did not ship the order.",
  },
  {
    value: "SELLER_NO_RESPONSE",
    label: "No Response",
    description: "Seller did not respond during an active dispute.",
  },
  {
    value: "SELLER_COUNTERFEIT_OR_SAFETY",
    label: "Counterfeit or Safety Issue",
    description: "Product may be counterfeit, unsafe, or prohibited.",
  },
  {
    value: "SELLER_STOCK_MISMATCH",
    label: "Stock Mismatch",
    description: "Product availability did not match the listing.",
  },
  {
    value: "SELLER_FRAUD",
    label: "Seller Fraud",
    description: "Suspected fraudulent seller activity.",
  },
  {
    value: "SELLER_FAKE_ACCOUNT",
    label: "Fake Account",
    description: "The seller account appears to use a false or misleading identity.",
  },
  {
    value: "SELLER_PLATFORM_POLICY_VIOLATION",
    label: "Platform Policy Violation",
    description: "The seller violated the platform's rules or terms of use.",
  },
];

const BUYER_STRIKE_CATEGORIES = [
  {
    value: "BUYER_UNPAID_FINAL_SALE",
    label: "Unpaid Final Sale",
    description: "Buyer failed to pay for a won final-sale item.",
  },
  {
    value: "BUYER_RETURN_ABUSE",
    label: "Return Abuse",
    description: "Suspicious or repeated misuse of the return process.",
  },
  {
    value: "BUYER_FRAUD",
    label: "Buyer Fraud",
    description: "Suspected fraudulent buyer activity.",
  },
  {
    value: "BUYER_BID_MANIPULATION",
    label: "Bid Manipulation",
    description: "Suspicious bidding behaviour or linked-account activity.",
  },
  {
    value: "BUYER_FAKE_ACCOUNT",
    label: "Fake Account",
    description: "The buyer account appears to use a false or misleading identity.",
  },
  {
    value: "BUYER_PLATFORM_POLICY_VIOLATION",
    label: "Platform Policy Violation",
    description: "The buyer violated the platform's rules or terms of use.",
  },
];

const STRIKE_CATEGORY_CONFIG = {
  SELLER_FAKE_ACCOUNT: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: false,
  },
  SELLER_PLATFORM_POLICY_VIOLATION: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: false,
  },
  BUYER_FAKE_ACCOUNT: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: false,
  },
  BUYER_PLATFORM_POLICY_VIOLATION: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: false,
  },
  SELLER_MISLEADING_PRODUCT: {
    referenceType: "PRODUCT",
    referenceLabel: "Select Product",
    referenceRequired: true,
    evidenceRequired: false,
  },
  SELLER_LATE_SHIPMENT: {
    referenceType: "ORDER",
    referenceLabel: "Select Order",
    referenceRequired: true,
    evidenceRequired: false,
  },
  SELLER_DOES_NOT_SHIP: {
    referenceType: "ORDER",
    referenceLabel: "Select Order",
    referenceRequired: true,
    evidenceRequired: false,
  },
  SELLER_NO_RESPONSE: {
    referenceType: "DISPUTE",
    referenceLabel: "Select Dispute",
    referenceRequired: true,
    evidenceRequired: false,
  },
  SELLER_COUNTERFEIT_OR_SAFETY: {
    referenceType: "PRODUCT",
    referenceLabel: "Select Product",
    referenceRequired: true,
    evidenceRequired: true,
  },
  SELLER_STOCK_MISMATCH: {
    referenceType: "PRODUCT",
    referenceLabel: "Select Product",
    referenceRequired: true,
    evidenceRequired: false,
  },
  SELLER_FRAUD: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: true,
  },
  BUYER_UNPAID_FINAL_SALE: {
    referenceType: "BID",
    referenceLabel: "Select Bid",
    referenceRequired: true,
    evidenceRequired: false,
  },
  BUYER_RETURN_ABUSE: {
    referenceType: "RETURN",
    referenceLabel: "Select Return",
    referenceRequired: true,
    evidenceRequired: false,
  },
  BUYER_FRAUD: {
    referenceType: "OPTIONAL",
    referenceLabel: "Select Related Record",
    referenceRequired: false,
    evidenceRequired: true,
  },
  BUYER_BID_MANIPULATION: {
    referenceType: "BID",
    referenceLabel: "Select Bid",
    referenceRequired: true,
    evidenceRequired: true,
  },
};

const SEVERITY_OPTIONS = [
  {
    value: "LOW",
    label: "Low",
    description: "Minor violation with limited impact.",
  },
  {
    value: "MEDIUM",
    label: "Medium",
    description: "Repeated or meaningful policy violation.",
  },
  {
    value: "HIGH",
    label: "High",
    description: "Serious violation requiring restrictions.",
  },
  {
    value: "CRITICAL",
    label: "Critical",
    description: "Fraud, safety, counterfeit, or major platform risk.",
  },
];

const OPTIONAL_REFERENCE_TYPES = [
  { value: "", label: "No related record" },
  { value: "PRODUCT", label: "Product" },
  { value: "ORDER", label: "Order" },
  { value: "DISPUTE", label: "Dispute" },
  { value: "RETURN", label: "Return" },
  { value: "BID", label: "Bid" },
];

const REFERENCE_TYPE_FETCH_CATEGORY = {
  PRODUCT: "SELLER_MISLEADING_PRODUCT",
  ORDER: "SELLER_LATE_SHIPMENT",
  DISPUTE: "SELLER_NO_RESPONSE",
  RETURN: "BUYER_RETURN_ABUSE",
  BID: "BUYER_UNPAID_FINAL_SALE",
};

const MAX_EVIDENCE_FILES = 10;
const HISTORY_BATCH_SIZE = 5;
const STRIKE_STATUS_PRIORITY = {
  ACTIVE: 1,
  UNDER_APPEAL: 2,
  EXPIRED: 3,
  OVERTURNED: 4,
  RESOLVED: 5,
  CLOSED: 6,
};
const STRIKE_STATUS_FILTERS = ["ALL", "ACTIVE", "EXPIRED", "OVERTURNED", "UNDER_APPEAL"];
const STRIKE_SEVERITY_FILTERS = ["ALL", "LOW", "MEDIUM", "HIGH", "CRITICAL"];
const RESTRICTION_STATUS_FILTERS = ["ALL", "ACTIVE", "REMOVED", "EXPIRED", "CLOSED", "INACTIVE"];
const MAX_EVIDENCE_FILE_SIZE = 10 * 1024 * 1024;
const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "U";
  return parts.slice(0, 2).map((part) => part[0]).join("").toUpperCase();
};

const formatBytes = (bytes) => {
  if (!bytes) return "0 KB";
  const units = ["Bytes", "KB", "MB", "GB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, index)).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
};

const getFileExtension = (fileName = "") => fileName.split(".").pop()?.toLowerCase() || "";

const isAcceptedEvidenceFile = (file) => {
  return file.type.startsWith("image/");
};

const getFileIcon = (file) => {
  if (file?.type?.startsWith("image/")) return <FileImage size={26} />;
  return <FileImage size={26} />;
};

const pickFirst = (item, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((source, path) => source?.[path], item);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const getReferenceId = (item, referenceType) => {
  if (referenceType === "BID") {
    return pickFirst(item, ["bid_id", "bid.id", "id", "_id"]);
  }

  return pickFirst(item, ["id", "item_id", "product_id", "order_id", "dispute_id", "return_id", "_id"]);
};

const normalizeReferenceItem = (item, referenceType) => {
  const id = getReferenceId(item, referenceType);
  const labelKeys = referenceType === "BID" ? [
    "bid_number",
    "bid_no",
    "bid_code",
    "product_title",
    "item_title",
    "product_name",
    "item_name",
    "product.title",
    "product.name",
    "title",
    "name",
  ] : [
    "title",
    "item_name",
    "product_name",
    "name",
    "order_number",
    "order_no",
    "dispute_number",
    "return_number",
    "sku",
    "product.title",
    "product.name",
  ];
  const label = pickFirst(item, labelKeys) || `${referenceType || "Record"} #${id}`;
  const codeKeys = referenceType === "BID" ? [
    "bid_number",
    "bid_no",
    "bid_code",
    "bid_id",
    "id",
    "_id",
  ] : [
    "sku",
    "code",
    "item_code",
    "order_number",
    "order_no",
    "dispute_number",
    "return_number",
    "tracking_number",
  ];
  const code = pickFirst(item, codeKeys) || `#${id}`;
  const status = pickFirst(item, ["bid_status", "status", "order_status", "dispute_status", "return_status", "item_status", "is_deactive"]);
  const price = pickFirst(item, ["bid_amount", "amount", "final_bid_price", "final_price", "price", "total", "order_total", "product.price"]);
  const date = pickFirst(item, ["bid_time", "created_at", "updated_at", "order_date", "date"]);
  const description = pickFirst(item, ["description", "reason", "subject", "message", "buyer_name", "user_name", "product.description"]);
  const metaParts = [description, price ? `EUR ${price}` : "", date].filter(Boolean);

  return {
    value: String(id),
    label: String(label),
    code: String(code),
    status: status ? String(status) : referenceType || "",
    meta: metaParts.join(" - ") || "-",
    raw: item,
    referenceType,
  };
};

const getUploadedImageReference = (response) => (
  response?.data?.data ||
  response?.data?.path?.[0] ||
  response?.path?.[0] ||
  response?.data?.url ||
  response?.url ||
  ""
);

const getFetchCategoryForReference = (category, referenceType) => {
  const config = STRIKE_CATEGORY_CONFIG[category];
  if (config?.referenceType === "OPTIONAL") {
    return REFERENCE_TYPE_FETCH_CATEGORY[referenceType] || category;
  }
  return category;
};

const getReferenceRows = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.rows)) return response.rows;
  if (Array.isArray(response?.items)) return response.items;
  if (Array.isArray(response?.results)) return response.results;
  return [];
};

const formatStrikeDate = (value) => {
  return formatDateTime(value, undefined, false, { hour: undefined, minute: undefined });
};

const getEvidenceUrl = (path = "") => {
  if (!path) return "";
  if (String(path).startsWith("http")) return path;
  return `${REACT_APP_IMAGE_URL || ""}${path}`;
};

const getRestrictionId = (restriction) => (
  restriction?.id ||
  restriction?.restriction_id ||
  restriction?.account_restriction_id ||
  restriction?._id ||
  ""
);

const getStrikeId = (strike, index) => (
  strike?.id ||
  strike?.strike_id ||
  `${strike?.category || "strike"}-${strike?.created_at || index}`
);

const normalizeValue = (value) => String(value || "").trim().toUpperCase();

const normalizeSearchText = (items) => items.filter(Boolean).join(" ").toLowerCase();

const getCreatedTime = (item) => {
  const value = item?.created_at || item?.createdAt || item?.updated_at || item?.updatedAt;
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getFirstValue = (source, keys) => {
  for (const key of keys) {
    const value = key.split(".").reduce((item, path) => item?.[path], source);
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return "";
};

const getRestrictionDateValue = (restriction, keys) => (
  getFirstValue(restriction, keys.map((key) => key.replace(/At$/, "_at"))) ||
  getFirstValue(restriction, keys)
);

const getRestrictionSortTime = (restriction) => {
  const value = getRestrictionDateValue(restriction, ["updatedAt", "createdAt"]);
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date.getTime() : 0;
};

const getReferencePlaceholder = (referenceType, t) => {
  switch (referenceType) {
    case "PRODUCT":
      return t("applyStrike.placeholders.searchProduct");
    case "ORDER":
      return t("applyStrike.placeholders.searchOrder");
    case "DISPUTE":
      return t("applyStrike.placeholders.searchDispute");
    case "BID":
      return t("applyStrike.placeholders.searchBid");
    case "RETURN":
      return t("applyStrike.placeholders.searchReturn");
    default:
      return t("applyStrike.placeholders.searchRelated");
  }
};

const ReferenceOption = (props) => {
  const { data, isFocused } = props;
  return (
    <components.Option {...props}>
      <div className={`strike-reference-option ${isFocused ? "focused" : ""}`}>
        <div className="strike-reference-option-main">
          <strong>{data.label}</strong>
          <span>{data.code}</span>
        </div>
        <div className="strike-reference-option-meta">
          <span className="strike-reference-status">{data.status}</span>
          <TranslateText text={data.meta} fallback="-" as="small" />
        </div>
      </div>
    </components.Option>
  );
};

const ReferenceSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="strike-reference-value">
      <strong>{props.data.label}</strong>
      <span>{props.data.code}</span>
    </div>
  </components.SingleValue>
);

const CategoryOption = (props) => (
  <components.Option {...props}>
    <div className="strike-category-option">
      <strong>{props.data.label}</strong>
      <TranslateText text={props.data.description} fallback="-" />
    </div>
  </components.Option>
);

const REFERENCE_TYPE_ICONS = {
  "": Ban,
  PRODUCT: Package,
  ORDER: Truck,
  DISPUTE: MessageSquareWarning,
  RETURN: RotateCcw,
  BID: Gavel,
};

const ReferenceTypeIcon = ({ value }) => {
  const Icon = REFERENCE_TYPE_ICONS[value] || Search;
  return (
    <span className={`strike-reftype-icon ${!value ? "strike-reftype-icon-none" : ""}`}>
      <Icon size={15} />
    </span>
  );
};

const ReferenceTypeOption = (props) => (
  <components.Option {...props}>
    <div className="strike-reftype-option">
      <ReferenceTypeIcon value={props.data.value} />
      <span className="strike-reftype-label">{props.data.label}</span>
    </div>
  </components.Option>
);

const ReferenceTypeSingleValue = (props) => (
  <components.SingleValue {...props}>
    <div className="strike-reftype-option strike-reftype-value">
      <ReferenceTypeIcon value={props.data.value} />
      <span className="strike-reftype-label">{props.data.label}</span>
    </div>
  </components.SingleValue>
);

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 46,
    borderWidth: 1.5,
    borderColor: state.isFocused ? "#e8671e" : "#e5e7eb",
    boxShadow: "none",
    cursor: "pointer",
  }),
  menu: (base) => ({ ...base, zIndex: 30, marginTop: 6 }),
  option: (base, state) => ({
    ...base,
    padding: 0,
    background: "transparent",
    color: "#16181d",
    cursor: "pointer",
  }),
  singleValue: (base) => ({ ...base, margin: 0 }),
  valueContainer: (base) => ({ ...base, padding: "2px 14px" }),
};

const UserStrike = ({ userData }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { strikeData, restrictionData } = useSelector(state => state.user);
  const [showStrikeForm, setShowStrikeForm] = useState(false);
  const [showRestrictionForm, setShowRestrictionForm] = useState(false);
  const [strikeForm, setStrikeForm] = useState(initialStrikeForm);
  const [restrictionForm, setRestrictionForm] = useState(initialRestrictionForm);
  const [errors, setErrors] = useState({});
  const [restrictionErrors, setRestrictionErrors] = useState({});
  const [referenceOptions, setReferenceOptions] = useState([]);
  const [referenceSearch, setReferenceSearch] = useState("");
  const [referenceLoading, setReferenceLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [optionalReferenceType, setOptionalReferenceType] = useState("");
  const [closeModalOpen, setCloseModalOpen] = useState(false);
  const [selectedStrike, setSelectedStrike] = useState(null);
  const [closeStrikeForm, setCloseStrikeForm] = useState({
    resolution_reason: "",
    resolution_notes: "",
  });
  const addBodyClass = () => document.body.classList.add("overflow-hidden");
  const removeBodyClass = () => document.body.classList.remove("overflow-hidden");
  const [closeStrikeErrors, setCloseStrikeErrors] = useState({});
  const [closeStrikeLoading, setCloseStrikeLoading] = useState(false);
  const [restrictionActionModalType, setRestrictionActionModalType] = useState("");
  const [selectedRestriction, setSelectedRestriction] = useState(null);
  const [restrictionActionReason, setRestrictionActionReason] = useState("");
  const [restrictionActionError, setRestrictionActionError] = useState("");
  const [restrictionSubmitLoading, setRestrictionSubmitLoading] = useState(false);
  const [restrictionActionLoading, setRestrictionActionLoading] = useState(false);
  const [restrictionLoading, setRestrictionLoading] = useState(false);
  const [restrictionLoaded, setRestrictionLoaded] = useState(false);
  const [restrictionError, setRestrictionError] = useState("");
  const [expandedStrikeId, setExpandedStrikeId] = useState(null);
  const [expandedRestrictionId, setExpandedRestrictionId] = useState(null);
  const [visibleStrikeCount, setVisibleStrikeCount] = useState(HISTORY_BATCH_SIZE);
  const [visibleRestrictionCount, setVisibleRestrictionCount] = useState(HISTORY_BATCH_SIZE);
  const [strikeSearch, setStrikeSearch] = useState("");
  const [strikeStatusFilter, setStrikeStatusFilter] = useState("ALL");
  const [strikeSeverityFilter, setStrikeSeverityFilter] = useState("ALL");
  const [restrictionSearch, setRestrictionSearch] = useState("");
  const [restrictionStatusFilter, setRestrictionStatusFilter] = useState("ALL");
  const fileInputRef = useRef(null);
  const formRef = useRef(null);
  const evidenceRef = useRef([]);
  const user = userData || {};
  const displayName = user?.name || user?.user_name || t("applyStrike.unknownUser");
  const normalizedRole = String(user?.role || "").toUpperCase();
  const isBuyerRole = normalizedRole === "BUYER";
  const normalizedStrikes = Array.isArray(strikeData) ? strikeData : [];
  const activeStrikeItems = normalizedStrikes.filter((item) => normalizeValue(item?.status) === "ACTIVE");
  const activeStrikes = normalizedStrikes.length
    ? activeStrikeItems.length
    : user?.active_strikes || user?.active_strike_count || 0;
  const hasCriticalActiveStrike = normalizedStrikes.some((item) => (
    normalizeValue(item?.status) === "ACTIVE" && normalizeValue(item?.severity) === "CRITICAL"
  ));
  const isRestrictionEligible = activeStrikes >= 3 || hasCriticalActiveStrike;
  const normalizedRestrictionData = Array.isArray(restrictionData)
    ? restrictionData
    : restrictionData
      ? [restrictionData]
      : [];
  const hasRestrictionHistory = normalizedRestrictionData.length > 0;
  const restriction = normalizedRestrictionData
    .slice()
    .sort((a, b) => getRestrictionSortTime(b) - getRestrictionSortTime(a))[0] || null;
  const canRenderRestrictionActions = restrictionLoaded && !restrictionLoading;
  const canApplyStrike = !isRestrictionEligible;
  const canApplyRestriction = canRenderRestrictionActions && isRestrictionEligible && !hasRestrictionHistory;
  const activeRestrictions = normalizedRestrictionData.filter((item) => normalizeValue(item?.status || item?.restriction_status) === "ACTIVE");
  const restrictionEligibilityReason = hasCriticalActiveStrike
    ? t("applyStrike.restriction.criticalThreshold")
    : t("applyStrike.restriction.countThreshold", { count: activeStrikes });
  const restrictionTypeOptions = useMemo(() => {
    const availableTypes = RESTRICTION_TYPES.filter((item) => {
      if (isBuyerRole) {
        return item.value === "ACCOUNT_SUSPENDED";
      }
      return true;
    });

    return availableTypes.map((item) => ({
      ...item,
      label: t(`applyStrike.restriction.types.${item.value}.label`, item.label),
      description: t(`applyStrike.restriction.types.${item.value}.description`, item.description),
    }));
  }, [t, isBuyerRole]);
  const selectedRestrictionType = restrictionTypeOptions.find((item) => item.value === restrictionForm.restriction_type) || null;
  const isRestrictionSubmitDisabled =
    restrictionSubmitLoading ||
    !restrictionForm.restriction_type ||
    !restrictionForm.reason.trim();

  function getStrikeCategoryLabel(category) {
    return t(`applyStrike.categories.${category}.label`, category || "-");
  }

  function getStrikeSeverityLabel(severity) {
    return t(`applyStrike.severity.${severity}.label`, severity || "-");
  }

  function getRestrictionTypeLabel(type) {
    return t(`applyStrike.restriction.types.${type}.label`, type || "-");
  }

  function getStrikeRelatedLabel(strike) {
    if (strike?.related_bid_id) return `${t("applyStrike.history.bid", "Bid")} #${strike.related_bid_id}`;
    if (strike?.related_return_id) return `${t("applyStrike.history.return", "Return")} #${strike.related_return_id}`;
    if (strike?.related_product_id) return `${t("applyStrike.history.product")} #${strike.related_product_id}`;
    if (strike?.related_order_id) return `${t("applyStrike.history.order")} #${strike.related_order_id}`;
    if (strike?.related_dispute_id) return `${t("applyStrike.history.dispute")} #${strike.related_dispute_id}`;
    return t("applyStrike.history.noReference");
  }

  const sortedStrikes = useMemo(() => (
    normalizedStrikes
      .slice()
      .sort((a, b) => {
        const statusDifference = (STRIKE_STATUS_PRIORITY[normalizeValue(a?.status)] || 99) - (STRIKE_STATUS_PRIORITY[normalizeValue(b?.status)] || 99);
        if (statusDifference !== 0) return statusDifference;
        return getCreatedTime(b) - getCreatedTime(a);
      })
  ), [normalizedStrikes]);

  const filteredStrikes = useMemo(() => {
    const search = strikeSearch.trim().toLowerCase();
    return sortedStrikes.filter((strike) => {
      const status = normalizeValue(strike?.status);
      const severity = normalizeValue(strike?.severity);
      const matchesStatus = strikeStatusFilter === "ALL" || status === strikeStatusFilter;
      const matchesSeverity = strikeSeverityFilter === "ALL" || severity === strikeSeverityFilter;
      const searchableText = normalizeSearchText([
        getStrikeCategoryLabel(strike?.category),
        strike?.category,
        strike?.reason,
        strike?.description,
        getStrikeRelatedLabel(strike),
        strike?.related_product_id,
        strike?.related_order_id,
        strike?.related_bid_id,
        strike?.related_return_id,
        strike?.related_dispute_id,
        strike?.created_by_name,
      ]);
      return matchesStatus && matchesSeverity && (!search || searchableText.includes(search));
    });
  }, [sortedStrikes, strikeSearch, strikeStatusFilter, strikeSeverityFilter, t]);

  const visibleStrikes = filteredStrikes.slice(0, visibleStrikeCount);
  const activeStrikeHistoryCount = normalizedStrikes.filter((strike) => normalizeValue(strike?.status) === "ACTIVE").length;

  const sortedRestrictions = useMemo(() => (
    normalizedRestrictionData
      .slice()
      .sort((a, b) => getRestrictionSortTime(b) - getRestrictionSortTime(a))
  ), [normalizedRestrictionData]);

  const filteredRestrictions = useMemo(() => {
    const search = restrictionSearch.trim().toLowerCase();
    return sortedRestrictions.filter((item) => {
      const status = normalizeValue(item?.status || item?.restriction_status);
      const matchesStatus = restrictionStatusFilter === "ALL" || status === restrictionStatusFilter;
      const searchableText = normalizeSearchText([
        getRestrictionTypeLabel(item?.restriction_type),
        item?.restriction_type,
        item?.reason,
        item?.description,
        item?.internal_note,
        item?.admin_note,
        item?.created_by_name,
        item?.closed_by_name,
      ]);
      return matchesStatus && (!search || searchableText.includes(search));
    });
  }, [sortedRestrictions, restrictionSearch, restrictionStatusFilter, t]);

  const visibleRestrictions = filteredRestrictions.slice(0, visibleRestrictionCount);

  useEffect(() => {
    setVisibleStrikeCount(HISTORY_BATCH_SIZE);
    setExpandedStrikeId(null);
  }, [strikeSearch, strikeStatusFilter, strikeSeverityFilter]);

  useEffect(() => {
    setVisibleRestrictionCount(HISTORY_BATCH_SIZE);
    setExpandedRestrictionId(null);
  }, [restrictionSearch, restrictionStatusFilter]);

  const fetchRestrictionData = async () => {
    if (!user?.id) return null;

    setRestrictionLoading(true);
    setRestrictionError("");
    try {
      const response = await get_restriction_data(dispatch, user.id);
      if (!response) {
        setRestrictionError(t("applyStrike.restriction.fetchFailed"));
      }
      return response;
    } catch (error) {
      setRestrictionError(t("applyStrike.restriction.fetchFailed"));
      return null;
    } finally {
      setRestrictionLoading(false);
      setRestrictionLoaded(true);
    }
  };

  const fetchData = async () => {
    if (!user?.id) return;
    await get_strike_data(dispatch, user.id);
    await fetchRestrictionData();
  };

  // call order data function 
  useEffect(() => {
    fetchData();
  }, [user?.id]);

  useEffect(() => {
    if (isRestrictionEligible) {
      setShowStrikeForm(false);
      removeBodyClass()
    } else {
      setShowRestrictionForm(false);
      removeBodyClass()
    }
  }, [isRestrictionEligible]);

  useEffect(() => {
    if (isBuyerRole && restrictionForm.restriction_type && restrictionForm.restriction_type !== "ACCOUNT_SUSPENDED") {
      setRestrictionForm((prev) => ({
        ...prev,
        restriction_type: "",
      }));
    }
  }, [isBuyerRole, restrictionForm.restriction_type]);

  const availableCategories = useMemo(() => {
    const translateCategory = (item) => ({
      ...item,
      label: t(`applyStrike.categories.${item.value}.label`, item.label),
      description: t(`applyStrike.categories.${item.value}.description`, item.description),
    });

    if (normalizedRole === "SELLER") return SELLER_STRIKE_CATEGORIES.map(translateCategory);
    if (normalizedRole === "BUYER") return BUYER_STRIKE_CATEGORIES.map(translateCategory);
    return [];
  }, [normalizedRole, t]);

  const severityOptions = useMemo(() => SEVERITY_OPTIONS.map((item) => ({
    ...item,
    label: t(`applyStrike.severity.${item.value}.label`, item.label),
    description: t(`applyStrike.severity.${item.value}.description`, item.description),
  })), [t]);

  const optionalReferenceTypes = useMemo(() => OPTIONAL_REFERENCE_TYPES.map((item) => ({
    ...item,
    label: t(`applyStrike.optionalReferenceTypes.${item.value || "NONE"}`, item.label),
  })), [t]);

  const selectedCategoryConfig = STRIKE_CATEGORY_CONFIG[strikeForm.category] || null;
  const selectedReferenceType = selectedCategoryConfig?.referenceType === "OPTIONAL"
    ? optionalReferenceType
    : selectedCategoryConfig?.referenceType;
  const selectedReference = referenceOptions.find((option) => option.value === strikeForm.reference_id) || null;
  const categorySelectedOption = availableCategories.find((option) => option.value === strikeForm.category) || null;
  const optionalReferenceOption = optionalReferenceTypes.find((option) => option.value === optionalReferenceType) || optionalReferenceTypes[0];

  const isStrikeFormIncomplete =
    !strikeForm.category ||
    !strikeForm.severity ||
    !strikeForm.reason.trim() ||
    !strikeForm.description.trim() ||
    (selectedCategoryConfig?.referenceRequired && !strikeForm.reference_id) ||
    (selectedCategoryConfig?.evidenceRequired && strikeForm.evidence.length === 0);

  useEffect(() => {
    if (!strikeForm.category) {
      setReferenceOptions([]);
      return undefined;
    }

    const config = STRIKE_CATEGORY_CONFIG[strikeForm.category];
    if (!config || config.referenceType === "OPTIONAL") return undefined;

    setReferenceLoading(true);
    const timer = setTimeout(async () => {
      const response = await get_Strike_Category_item(dispatch, strikeForm.category, user?.id, referenceSearch);
      const rows = getReferenceRows(response);
      setReferenceOptions(rows.map((item) => normalizeReferenceItem(item, config.referenceType)).filter((item) => item.value));
      setReferenceLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, referenceSearch, strikeForm.category, user?.id]);

  useEffect(() => {
    if (!optionalReferenceType) {
      if (selectedCategoryConfig?.referenceType === "OPTIONAL") setReferenceOptions([]);
      return undefined;
    }

    setReferenceLoading(true);
    const timer = setTimeout(async () => {
      const fetchCategory = getFetchCategoryForReference(strikeForm.category, optionalReferenceType);
      const response = await get_Strike_Category_item(dispatch, fetchCategory, user?.id, referenceSearch);
      const rows = getReferenceRows(response);
      setReferenceOptions(rows.map((item) => normalizeReferenceItem(item, optionalReferenceType)).filter((item) => item.value));
      setReferenceLoading(false);
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, optionalReferenceType, referenceSearch, selectedCategoryConfig?.referenceType, strikeForm.category, user?.id]);

  useEffect(() => {
    evidenceRef.current = strikeForm.evidence;
  }, [strikeForm.evidence]);

  useEffect(() => {
    return () => {
      evidenceRef.current.forEach((item) => {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, []);

  const updateField = (field, value) => {
    setStrikeForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCategoryChange = (value) => {
    const config = STRIKE_CATEGORY_CONFIG[value];

    strikeForm.evidence.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });

    setStrikeForm({
      ...initialStrikeForm,
      category: value,
      reference_type: config?.referenceType === "OPTIONAL" ? "" : config?.referenceType || "",
    });
    setOptionalReferenceType("");
    setReferenceSearch("");
    setReferenceOptions([]);
    setErrors({});
    setDragActive(false);
  };

  const resetStrikeForm = () => {
    strikeForm.evidence.forEach((item) => {
      if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    });

    setStrikeForm(initialStrikeForm);
    setOptionalReferenceType("");
    setReferenceSearch("");
    setReferenceOptions([]);
    setErrors({});
    setDragActive(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCancel = () => {
    resetStrikeForm();
    setShowStrikeForm(false);
    removeBodyClass()
  };

  const updateRestrictionField = (field, value) => {
    setRestrictionForm((prev) => ({ ...prev, [field]: value }));
    setRestrictionErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const resetRestrictionForm = () => {
    setRestrictionForm(initialRestrictionForm);
    setRestrictionErrors({});
    setSelectedRestriction(null);
  };

  const handleRestrictionCancel = () => {
    resetRestrictionForm();
    setShowRestrictionForm(false);
    removeBodyClass()
  };

  const openRestrictionCreateForm = () => {
    resetRestrictionForm();
    setShowRestrictionForm(true);
    addBodyClass()
  };

  const createEvidenceItem = (file) => ({
    id: `${file.name}-${file.size}-${file.lastModified}`,
    file,
    previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
  });

  const addEvidenceFiles = (files) => {
    if (!strikeForm.category) return;

    const validFiles = [];
    const rejectedMessages = [];
    const existingIds = new Set(strikeForm.evidence.map((item) => item.id));

    files.forEach((file) => {
      const id = `${file.name}-${file.size}-${file.lastModified}`;
      if (existingIds.has(id) || validFiles.some((item) => `${item.name}-${item.size}-${item.lastModified}` === id)) return;
      if (strikeForm.evidence.length + validFiles.length >= MAX_EVIDENCE_FILES) {
        rejectedMessages.push(t("applyStrike.errors.maxFiles"));
        return;
      }
      if (!file.size) {
        rejectedMessages.push(t("applyStrike.errors.emptyFile", { file: file.name }));
        return;
      }
      if (file.size > MAX_EVIDENCE_FILE_SIZE) {
        rejectedMessages.push(t("applyStrike.errors.fileTooLarge", { file: file.name }));
        return;
      }
      if (!isAcceptedEvidenceFile(file)) {
        rejectedMessages.push(t("applyStrike.errors.unsupportedFile", { file: file.name }));
        return;
      }
      validFiles.push(file);
    });

    if (rejectedMessages.length) {
      const uniqueMessages = [...new Set(rejectedMessages)];
      uniqueMessages.forEach((message) => toast.error(message, { autoClose: 2500 }));
    }

    if (validFiles.length) {
      setStrikeForm((prev) => ({
        ...prev,
        evidence: [...prev.evidence, ...validFiles.map(createEvidenceItem)],
      }));
      setErrors((prev) => ({ ...prev, evidence: "" }));
    }
  };

  const handleEvidenceChange = (event) => {
    addEvidenceFiles(Array.from(event.target.files || []));
    event.target.value = "";
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    if (!strikeForm.category) return;
    setDragActive(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragActive(false);
    if (!strikeForm.category) return;
    addEvidenceFiles(Array.from(event.dataTransfer.files || []));
  };

  const removeEvidence = (id) => {
    setStrikeForm((prev) => {
      const removedItem = prev.evidence.find((item) => item.id === id);
      if (removedItem?.previewUrl) URL.revokeObjectURL(removedItem.previewUrl);
      return {
        ...prev,
        evidence: prev.evidence.filter((item) => item.id !== id),
      };
    });
  };

  const scrollToFirstError = (newErrors) => {
    const firstKey = Object.keys(newErrors)[0];
    if (!firstKey || !formRef.current) return;
    const target = formRef.current.querySelector(`[data-field="${firstKey}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const validateStrikeForm = () => {
    const newErrors = {};
    const config = STRIKE_CATEGORY_CONFIG[strikeForm.category];

    if (!strikeForm.category) newErrors.category = t("applyStrike.errors.categoryRequired");
    if (!strikeForm.severity) newErrors.severity = t("applyStrike.errors.severityRequired");
    if (!strikeForm.reason.trim()) {
      newErrors.reason = t("applyStrike.errors.reasonRequired");
    } else if (strikeForm.reason.trim().length < 3) {
      newErrors.reason = t("applyStrike.errors.reasonMin");
    } else if (strikeForm.reason.trim().length > 255) {
      newErrors.reason = t("applyStrike.errors.reasonMax");
    }

    if (!strikeForm.description.trim()) {
      newErrors.description = t("applyStrike.errors.descriptionRequired");
    } else if (strikeForm.description.trim().length < 10) {
      newErrors.description = t("applyStrike.errors.descriptionMin");
    } else if (strikeForm.description.trim().length > 2000) {
      newErrors.description = t("applyStrike.errors.descriptionMax");
    }

    if (config?.referenceRequired && !strikeForm.reference_id) {
      newErrors.reference_id = t("applyStrike.errors.referenceRequired", {
        label: t(`applyStrike.referenceLabels.${config.referenceType}`, config.referenceLabel),
      });
    }

    if (config?.evidenceRequired && strikeForm.evidence.length === 0) {
      newErrors.evidence = t("applyStrike.errors.evidenceRequired");
    }

    setErrors(newErrors);
    scrollToFirstError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRelatedPayload = () => {
    const referenceType = strikeForm.reference_type || selectedReferenceType;
    const referenceId = strikeForm.reference_id;

    if (!referenceId) return {};
    if (referenceType === "ORDER") return { related_order_id: referenceId };
    if (referenceType === "PRODUCT") return { related_product_id: referenceId };
    if (referenceType === "DISPUTE") return { related_dispute_id: referenceId };
    if (referenceType === "RETURN") return { related_return_id: referenceId };
    if (referenceType === "BID") return { related_bid_id: referenceId };

    return {};
  };

  const uploadEvidenceImages = async () => {
    const uploadedImages = [];

    for (const item of strikeForm.evidence) {
      const response = await upload_image(item.file, dispatch);
      const imageReference = getUploadedImageReference(response);
      if (!imageReference) {
        throw new Error(t("applyStrike.errors.uploadFailed", { file: item.file.name }));
      }
      uploadedImages.push(imageReference);
    }

    return uploadedImages;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStrikeForm()) return;

    setSubmitLoading(true);
    try {
      const uploadedImages = await uploadEvidenceImages();
      const payload = {
        account_id: user?.id,
        category: strikeForm.category,
        severity: strikeForm.severity,
        reason: strikeForm.reason.trim(),
        description: strikeForm.description.trim(),
        evidence_reference: uploadedImages,
        ...getRelatedPayload(),
      };

      const applied = await apply_strike(dispatch, payload);
      if (applied) {
        fetchData();
        resetStrikeForm();
        setShowStrikeForm(false);
        removeBodyClass()
      }
    } catch (error) {
      toast.error(error?.message || t("applyStrike.errors.submitFailed"), {
        toastId: "apply-strike-submit-error",
        autoClose: 2000,
      });
    } finally {
      setSubmitLoading(false);
    }
  };

  const validateRestrictionForm = () => {
    const nextErrors = {};

    if (!restrictionForm.restriction_type) {
      nextErrors.restriction_type = t("applyStrike.restriction.errors.typeRequired");
    }

    if (!restrictionForm.reason.trim()) {
      nextErrors.reason = t("applyStrike.restriction.errors.reasonRequired");
    }

    setRestrictionErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleRestrictionSubmit = async (event) => {
    event.preventDefault();
    if (!validateRestrictionForm()) return;
    if (hasRestrictionHistory) {
      toast.error(t("applyStrike.restriction.errors.alreadyExists"), {
        toastId: "restriction-already-exists",
        autoClose: 2000,
      });
      return;
    }

    setRestrictionSubmitLoading(true);
    try {
      const payload = {
        account_id: user?.id,
        restriction_type: restrictionForm.restriction_type,
        reason: restrictionForm.reason.trim(),
        description: restrictionForm.description.trim() || "",
        permanent: Boolean(restrictionForm.permanent),
        expires_at: restrictionForm.permanent || !restrictionForm.expires_at
          ? null
          : new Date(`${restrictionForm.expires_at}T23:59:59.000Z`).toISOString(),
      };

      const saved = await apply_restriction(dispatch, payload);
      if (saved) {
        await fetchRestrictionData();
        resetRestrictionForm();
        setShowRestrictionForm(false);
        removeBodyClass()
      }
    } finally {
      setRestrictionSubmitLoading(false);
    }
  };

  const openCloseStrikeModal = (strike) => {
    setSelectedStrike(strike);
    setCloseStrikeForm({
      resolution_reason: "",
      resolution_notes: "",
    });
    setCloseStrikeErrors({});
    setCloseModalOpen(true);
    addBodyClass()
  };

  const handleCloseStrikeModal = () => {
    setCloseModalOpen(false);
    removeBodyClass()
    setSelectedStrike(null);
    setCloseStrikeForm({
      resolution_reason: "",
      resolution_notes: "",
    });
    setCloseStrikeErrors({});
  };

  const handleCloseStrikeSubmit = async (event) => {
    event.preventDefault();
    if (closeStrikeLoading) return;

    const nextErrors = {};

    if (!closeStrikeForm.resolution_reason.trim()) {
      nextErrors.resolution_reason = t("applyStrike.history.resolutionReasonRequired");
    }

    if (!closeStrikeForm.resolution_notes.trim()) {
      nextErrors.resolution_notes = t("applyStrike.history.resolutionNotesRequired");
    }

    setCloseStrikeErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const strikeId = selectedStrike?.id;
    const payload = {
      // strike_id: selectedStrike?.id,
      // account_id: selectedStrike?.account_id || user?.id,
      // status: "RESOLVED",
      resolution_reason: closeStrikeForm.resolution_reason.trim(),
      notes: closeStrikeForm.resolution_notes.trim(),
    };

    setCloseStrikeLoading(true);
    try {
      const closed = await close_strike(dispatch, strikeId, payload);
      if (closed) {
        await fetchData();
        handleCloseStrikeModal();
      }
    } finally {
      setCloseStrikeLoading(false);
    }
  };

  const openRestrictionActionModal = (restriction, type) => {
    setSelectedRestriction(restriction);
    setRestrictionActionModalType(type);
    setRestrictionActionReason("");
    setRestrictionActionError("");
    addBodyClass()
  };

  const handleRestrictionActionModal = () => {
    if (restrictionActionLoading) return;
    setRestrictionActionModalType("");
    removeBodyClass()
    setSelectedRestriction(null);
    setRestrictionActionReason("");
    setRestrictionActionError("");
  };

  const handleRestrictionActionReasonChange = (value) => {
    setRestrictionActionReason(value);
    setRestrictionActionError("");
  };

  const handleRestrictionActionSubmit = async (event) => {
    event.preventDefault();
    if (restrictionActionLoading) return;

    if (!restrictionActionReason.trim()) {
      setRestrictionActionError(t("applyStrike.restriction.errors.reasonRequired"));
      return;
    }

    const restrictionId = getRestrictionId(selectedRestriction);
    const payload = {
      account_id: user?.id,
      restriction_type: selectedRestriction?.restriction_type || "",
      restriction_id: restrictionId,
      reason: restrictionActionReason.trim(),
    };

    setRestrictionActionLoading(true);
    try {
      const completed = await change_restriction_status(dispatch, restrictionActionModalType, restrictionId, payload);
      if (completed) {
        await fetchRestrictionData();
        setRestrictionActionModalType("");
        removeBodyClass()
        setSelectedRestriction(null);
        setRestrictionActionReason("");
        setRestrictionActionError("");
      }
    } finally {
      setRestrictionActionLoading(false);
    }
  };

  const profileImage = user?.profile_image || user?.profile_pic;
  const profileImageSrc = profileImage?.startsWith?.("http")
    ? profileImage
    : profileImage
      ? `${REACT_APP_IMAGE_URL}${profileImage}`
      : "";
  return (
    <div className="strike-shell">
      <div className="strike-trigger-card">
        <div>
          <span className="strike-kicker">{t("applyStrike.moderation")}</span>
          <h3>{t("applyStrike.activeStrikes", { count: activeStrikes })}</h3>
          <p>
            {isRestrictionEligible
              ? t("applyStrike.restriction.triggerDescription")
              : t("applyStrike.triggerDescription")}
          </p>
          {isRestrictionEligible ? (
            <div className="restriction-eligibility-alert">
              <ShieldAlert size={16} />
              <span>{t("applyStrike.restriction.eligibilityMessage")}</span>
            </div>
          ) : null}
          {restrictionLoading ? (
            <div className="restriction-inline-state">
              <i className="fas fa-spinner fa-spin" />
              <span>{t("applyStrike.restriction.loading")}</span>
            </div>
          ) : null}
          {restrictionError ? (
            <div className="restriction-inline-error">
              <AlertTriangle size={16} />
              <span>{restrictionError}</span>
              <button type="button" onClick={fetchRestrictionData}>
                {t("applyStrike.restriction.retry")}
              </button>
            </div>
          ) : null}
        </div>
        {canApplyStrike || canApplyRestriction ? (
          <button
            type="button"
            className="strike-trigger-btn"
            onClick={() => {
              if (canApplyRestriction) {
                if (showRestrictionForm) {
                  handleRestrictionCancel();
                } else {
                  openRestrictionCreateForm();
                }
              } else if (canApplyStrike) {
                setShowStrikeForm(true);
                addBodyClass()
              }
            }}
            aria-expanded={canApplyRestriction ? showRestrictionForm : showStrikeForm}
          >
            {(canApplyRestriction ? showRestrictionForm : showStrikeForm) ? <X size={17} /> : <ShieldAlert size={17} />}
            {(canApplyRestriction ? showRestrictionForm : showStrikeForm)
              ? t("applyStrike.close")
              : canApplyRestriction
                ? t("applyStrike.restriction.title")
                : t("applyStrike.title")}
          </button>
        ) : null}
      </div>

      {showStrikeForm && canApplyStrike ? (
        <AddStrikeModal
          t={t}
          formRef={formRef}
          fileInputRef={fileInputRef}
          profileImageSrc={profileImageSrc}
          displayName={displayName}
          getInitials={getInitials}
          user={user}
          normalizedRole={normalizedRole}
          activeStrikes={activeStrikes}
          strikeForm={strikeForm}
          errors={errors}
          categorySelectedOption={categorySelectedOption}
          availableCategories={availableCategories}
          handleCategoryChange={handleCategoryChange}
          CategoryOption={CategoryOption}
          selectStyles={selectStyles}
          selectedCategoryConfig={selectedCategoryConfig}
          severityOptions={severityOptions}
          updateField={updateField}
          optionalReferenceTypes={optionalReferenceTypes}
          optionalReferenceOption={optionalReferenceOption}
          optionalReferenceType={optionalReferenceType}
          setOptionalReferenceType={setOptionalReferenceType}
          setReferenceSearch={setReferenceSearch}
          setStrikeForm={setStrikeForm}
          setErrors={setErrors}
          ReferenceTypeOption={ReferenceTypeOption}
          ReferenceTypeSingleValue={ReferenceTypeSingleValue}
          selectedReferenceType={selectedReferenceType}
          referenceOptions={referenceOptions}
          selectedReference={selectedReference}
          referenceSearch={referenceSearch}
          referenceLoading={referenceLoading}
          ReferenceOption={ReferenceOption}
          ReferenceSingleValue={ReferenceSingleValue}
          getReferencePlaceholder={getReferencePlaceholder}
          dragActive={dragActive}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          handleEvidenceChange={handleEvidenceChange}
          getFileIcon={getFileIcon}
          getFileExtension={getFileExtension}
          formatBytes={formatBytes}
          removeEvidence={removeEvidence}
          maxEvidenceFiles={MAX_EVIDENCE_FILES}
          isStrikeFormIncomplete={isStrikeFormIncomplete}
          submitLoading={submitLoading}
          onClose={handleCancel}
          onSubmit={handleSubmit}
        />
      ) : null}

      {showRestrictionForm && canApplyRestriction ? (
        <AddRestrictionModal
          t={t}
          restrictionForm={restrictionForm}
          restrictionErrors={restrictionErrors}
          selectedRestrictionType={selectedRestrictionType}
          restrictionTypeOptions={restrictionTypeOptions}
          CategoryOption={CategoryOption}
          selectStyles={selectStyles}
          updateRestrictionField={updateRestrictionField}
          setRestrictionForm={setRestrictionForm}
          restrictionEligibilityReason={restrictionEligibilityReason}
          isRestrictionSubmitDisabled={isRestrictionSubmitDisabled}
          restrictionSubmitLoading={restrictionSubmitLoading}
          onClose={handleRestrictionCancel}
          onSubmit={handleRestrictionSubmit}
        />
      ) : null}

      <section className="strike-history-card history-section">
        <div className="strike-history-header history-section-header">
          <div>
            <span className="strike-kicker">{t("applyStrike.history.kicker")}</span>
            <h3>{t("applyStrike.history.title")}</h3>
            <p>{t("applyStrike.history.subtitle")}</p>
          </div>
          <div className="history-count-row">
            <div className="strike-history-counts">
              <span>{t("applyStrike.history.total")}</span>
              <strong>{normalizedStrikes.length}</strong>
            </div>
            <div className="strike-history-counts history-count-muted">
              <span>{t("applyStrike.restriction.history.active", "Active")}</span>
              <strong>{activeStrikeHistoryCount}</strong>
            </div>
          </div>
        </div>

        <div className="history-toolbar" aria-label={t("applyStrike.history.title")}>
          <input
            className="history-search"
            value={strikeSearch}
            onChange={(event) => setStrikeSearch(event.target.value)}
            placeholder={t("applyStrike.history.searchPlaceholder", "Search strikes...")}
          />
          <select className="history-filter" value={strikeStatusFilter} onChange={(event) => setStrikeStatusFilter(event.target.value)}>
            {STRIKE_STATUS_FILTERS.map((item) => (
              <option key={item} value={item}>{item === "ALL" ? t("All", "All statuses") : item.replace("_", " ")}</option>
            ))}
          </select>
          <select className="history-filter" value={strikeSeverityFilter} onChange={(event) => setStrikeSeverityFilter(event.target.value)}>
            {STRIKE_SEVERITY_FILTERS.map((item) => (
              <option key={item} value={item}>{item === "ALL" ? t("All", "All severities") : item}</option>
            ))}
          </select>
          {expandedStrikeId ? (
            <button type="button" className="history-ghost-btn" onClick={() => setExpandedStrikeId(null)}>
              {t("Collapse", "Collapse all")}
            </button>
          ) : null}
        </div>

        {normalizedStrikes.length === 0 ? (
          <div className="strike-history-empty history-empty-compact">
            <ShieldAlert size={18} />
            <div>
              <strong>{t("applyStrike.history.empty", "No strikes recorded")}</strong>
              <span>{t("applyStrike.history.emptyHelper", "This user does not currently have any strike history.")}</span>
            </div>
          </div>
        ) : filteredStrikes.length === 0 ? (
          <div className="strike-history-empty history-empty-compact">
            <Search size={18} />
            <div>
              <strong>{t("applyStrike.history.noMatches", "No matching strikes")}</strong>
              <span>{t("applyStrike.history.noMatchesHelper", "Try changing the search text or filters.")}</span>
            </div>
          </div>
        ) : (
          <>
            <div className="history-list">
              {visibleStrikes.map((strike, index) => {
                const strikeId = getStrikeId(strike, index);
                const status = normalizeValue(strike?.status);
                const severity = normalizeValue(strike?.severity);
                const isExpanded = expandedStrikeId === strikeId;
                const evidenceItems = Array.isArray(strike?.evidence_reference) ? strike.evidence_reference : [];
                const visibleEvidence = evidenceItems.slice(0, 4);
                const hiddenEvidenceCount = Math.max(evidenceItems.length - visibleEvidence.length, 0);
                const detailsId = `strike-history-details-${strikeId}`;
                const toggleStrike = () => setExpandedStrikeId((current) => current === strikeId ? null : strikeId);

                return (
                  <article className={`history-accordion-item ${isExpanded ? "expanded" : ""}`} key={strikeId}>
                    <button
                      type="button"
                      className="history-accordion-summary"
                      onClick={toggleStrike}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          toggleStrike();
                        }
                      }}
                      aria-expanded={isExpanded}
                      aria-controls={detailsId}
                    >
                      <span className="history-icon-badge"><ShieldAlert size={17} /></span>
                      <span className="history-summary-primary">
                        <span className="history-summary-title">{getStrikeCategoryLabel(strike?.category)}</span>
                        <span className="history-summary-description">{strike?.reason || "-"}</span>
                        <span className="history-summary-reference">{getStrikeRelatedLabel(strike)}</span>
                      </span>
                      <span className="history-summary-meta">
                        <span><strong>{t("applyStrike.history.level")}</strong>{strike?.strike_level || "-"}</span>
                        <span><strong>{t("applyStrike.history.createdAt")}</strong>{formatStrikeDate(strike?.created_at)}</span>
                        <span><strong>{t("applyStrike.history.expiresAt")}</strong>{formatStrikeDate(strike?.expires_at)}</span>
                      </span>
                      <span className="history-summary-badges">
                        <span className={`strike-severity-badge strike-severity-${severity.toLowerCase() || "unknown"}`}>{getStrikeSeverityLabel(strike?.severity)}</span>
                        <span className={`strike-status-badge strike-status-${status.toLowerCase().replace(/_/g, "-") || "unknown"}`}>{status || "-"}</span>
                      </span>
                      {status === "ACTIVE" ? (
                        <span
                          role="button"
                          tabIndex={0}
                          className="history-compact-action history-danger-action"
                          onClick={(event) => {
                            event.stopPropagation();
                            openCloseStrikeModal(strike);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              event.stopPropagation();
                              openCloseStrikeModal(strike);
                            }
                          }}
                        >
                          {t("applyStrike.history.closeStrike")}
                        </span>
                      ) : null}
                      <ChevronDown className="history-chevron" size={18} />
                    </button>

                    <div className="history-accordion-content" id={detailsId}>
                      <div className="history-accordion-inner">
                        <div className="history-details">
                          <div className="history-detail-block history-detail-block-wide">
                            <h4>{t("applyStrike.violationDetails", "Violation details")}</h4>
                            <div className="history-detail-copy">
                              <strong>{t("applyStrike.reason")}</strong>
                              <p>{strike?.reason || "-"}</p>
                            </div>
                            <div className="history-detail-copy">
                              <strong>{t("applyStrike.description")}</strong>
                              <TranslateText text={strike?.description} fallback="-" />
                            </div>
                          </div>

                          <div className="history-detail-grid">
                            {[
                              [t("applyStrike.history.level"), strike?.strike_level],
                              [t("applyStrike.category"), getStrikeCategoryLabel(strike?.category)],
                              [t("applyStrike.severityLabel"), getStrikeSeverityLabel(strike?.severity)],
                              [t("Status", "Status"), status || "-"],
                              [t("applyStrike.history.reference"), getStrikeRelatedLabel(strike)],
                              [t("applyStrike.history.createdBy"), strike?.created_by_name || "-"],
                              [t("applyStrike.history.createdAt"), formatStrikeDate(strike?.created_at)],
                              [t("applyStrike.history.expiresAt"), formatStrikeDate(strike?.expires_at)],
                              [t("applyStrike.history.resolvedBy"), strike?.resolved_by_name || "-"],
                            ].filter((item) => item[1] && item[1] !== "-").map(([label, value]) => (
                              <div className="history-detail-item" key={label}>
                                <span>{label}</span>
                                <strong>{value}</strong>
                              </div>
                            ))}
                          </div>

                          {strike?.resolution_reason || strike?.resolution_notes ? (
                            <div className="history-resolution-compact">
                              <strong>{strike?.resolution_reason || "-"}</strong>
                              <TranslateText text={strike?.resolution_notes} fallback="-" as="p" />
                            </div>
                          ) : null}

                          {evidenceItems.length > 0 ? (
                            <div className="history-evidence-panel">
                              <div className="history-evidence-title">
                                <strong>{t("applyStrike.evidence")}</strong>
                                <span>{t("applyStrike.fileCount", { count: evidenceItems.length, max: evidenceItems.length })}</span>
                              </div>
                              <div className="history-evidence-grid">
                                {visibleEvidence.map((item, evidenceIndex) => {
                                  const evidenceUrl = getEvidenceUrl(item);
                                  const fileExt = getFileExtension(String(item)).toUpperCase() || "FILE";
                                  const isImage = /\.(png|jpe?g|webp|gif|avif)$/i.test(String(item));
                                  return (
                                    <a
                                      key={`${item}-${evidenceIndex}`}
                                      href={evidenceUrl}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="history-evidence-thumb"
                                      title={String(item)}
                                      onClick={(event) => event.stopPropagation()}
                                    >
                                      {isImage ? <img src={evidenceUrl} alt={t("applyStrike.history.evidenceAlt", { count: evidenceIndex + 1 })} loading="lazy" /> : <span>{fileExt}</span>}
                                      {hiddenEvidenceCount && evidenceIndex === visibleEvidence.length - 1 ? <em>+{hiddenEvidenceCount}</em> : null}
                                    </a>
                                  );
                                })}
                              </div>
                            </div>
                          ) : null}

                          {status === "ACTIVE" ? (
                            <div className="history-detail-actions">
                              <button type="button" className="strike-history-close-btn" onClick={() => openCloseStrikeModal(strike)}>
                                {t("applyStrike.history.closeStrike")}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <div className="history-show-more">
              {filteredStrikes.length > HISTORY_BATCH_SIZE ? (
                visibleStrikeCount < filteredStrikes.length ? (
                  <button type="button" onClick={() => setVisibleStrikeCount((count) => count + HISTORY_BATCH_SIZE)}>
                    {t("ShowMore", "Show 5 more")}
                  </button>
                ) : (
                  <button type="button" onClick={() => setVisibleStrikeCount(HISTORY_BATCH_SIZE)}>
                    {t("ShowLess", "Show less")}
                  </button>
                )
              ) : null}
            </div>
          </>
        )}
      </section>

      {hasRestrictionHistory ? (
        <section className="strike-history-card history-section restriction-history-card">
          <div className="strike-history-header history-section-header">
            <div>
              <span className="strike-kicker">{t("applyStrike.restriction.history.kicker")}</span>
              <h3>{t("applyStrike.restriction.history.title")}</h3>
              <p>{t("applyStrike.restriction.history.subtitle")}</p>
            </div>
            <div className="history-count-row">
              <div className="strike-history-counts">
                <span>{t("applyStrike.restriction.history.active")}</span>
                <strong>{activeRestrictions.length}</strong>
              </div>
              <div className="strike-history-counts history-count-muted">
                <span>{t("applyStrike.history.total")}</span>
                <strong>{normalizedRestrictionData.length}</strong>
              </div>
            </div>
          </div>

          {filteredRestrictions.length === 0 ? (
            <div className="strike-history-empty history-empty-compact">
              <Search size={18} />
              <div>
                <strong>{t("applyStrike.restriction.history.noMatches", "No matching restrictions")}</strong>
                <span>{t("applyStrike.history.noMatchesHelper", "Try changing the search text or filters.")}</span>
              </div>
            </div>
          ) : (
            <>
              <div className="history-list">
                {visibleRestrictions.map((item, index) => {
                  const itemId = getRestrictionId(item) || `${item?.restriction_type || "restriction"}-${index}`;
                  const itemStatus = normalizeValue(item?.status || item?.restriction_status);
                  const isExpanded = expandedRestrictionId === itemId;
                  const isActive = itemStatus === "ACTIVE";
                  const isRestorable = ["REMOVED", "CLOSED", "INACTIVE"].includes(itemStatus);
                  const itemMetaRows = [
                    [t("applyStrike.restriction.meta.permanent"), item?.permanent ? t("Yes", "Yes") : item?.permanent === false ? t("No", "No") : ""],
                    [t("applyStrike.restriction.meta.startedAt"), formatStrikeDate(item?.started_at || item?.startedAt || item?.created_at)],
                    [t("applyStrike.restriction.meta.expiresAt"), formatStrikeDate(item?.expires_at || item?.expiresAt)],
                    [t("applyStrike.restriction.meta.createdAt"), formatStrikeDate(item?.created_at || item?.createdAt)],
                    [t("applyStrike.restriction.meta.updatedAt"), formatStrikeDate(item?.updated_at || item?.updatedAt)],
                    [t("applyStrike.restriction.meta.createdBy"), item?.created_by_name || item?.created_by || item?.admin_name],
                    [t("applyStrike.restriction.meta.closedAt"), formatStrikeDate(item?.closed_at || item?.closedAt || item?.removed_at)],
                    [t("applyStrike.restriction.meta.closedBy"), item?.closed_by_name || item?.closed_by || item?.removed_by_name],
                  ].filter((row) => row[1] && row[1] !== "-");
                  const toggleRestriction = () => setExpandedRestrictionId((current) => current === itemId ? null : itemId);

                  return (
                    <article className={`history-accordion-item restriction-history-item ${isExpanded ? "expanded" : ""}`} key={itemId}>
                      <button
                        type="button"
                        className="history-accordion-summary"
                        onClick={toggleRestriction}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleRestriction();
                          }
                        }}
                        aria-expanded={isExpanded}
                        aria-controls={`restriction-history-details-${itemId}`}
                      >
                        <span className="history-icon-badge"><ShieldAlert size={17} /></span>
                        <span className="history-summary-primary">
                          <span className="history-summary-title">{getRestrictionTypeLabel(item?.restriction_type)}</span>
                          <span className="history-summary-description">{item?.reason || "-"}</span>
                          <span className="history-summary-reference">{item?.permanent ? t("applyStrike.restriction.permanent") : formatStrikeDate(item?.expires_at || item?.expiresAt)}</span>
                        </span>
                        <span className="history-summary-meta">
                          <span><strong>{t("applyStrike.restriction.meta.startedAt")}</strong>{formatStrikeDate(item?.started_at || item?.startedAt || item?.created_at)}</span>
                          <span><strong>{t("applyStrike.restriction.meta.expiresAt")}</strong>{item?.permanent ? t("applyStrike.restriction.permanent") : formatStrikeDate(item?.expires_at || item?.expiresAt)}</span>
                        </span>
                        <span className="history-summary-badges">
                          <span className={`strike-status-badge strike-status-${itemStatus.toLowerCase().replace(/_/g, "-") || "unknown"}`}>{itemStatus || "-"}</span>
                          <span className="strike-status-badge history-neutral-badge">{item?.permanent ? t("applyStrike.restriction.permanent") : t("applyStrike.restriction.temporary", "Temporary")}</span>
                        </span>
                        {isActive ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="history-compact-action history-danger-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openRestrictionActionModal(item, "close");
                            }}
                          >
                            {t("applyStrike.restriction.history.closeRestriction")}
                          </span>
                        ) : null}
                        {isRestorable ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="history-compact-action"
                            onClick={(event) => {
                              event.stopPropagation();
                              openRestrictionActionModal(item, "restore");
                            }}
                          >
                            {t("applyStrike.restriction.history.restoreRestriction")}
                          </span>
                        ) : null}
                        <ChevronDown className="history-chevron" size={18} />
                      </button>

                      <div className="history-accordion-content" id={`restriction-history-details-${itemId}`}>
                        <div className="history-accordion-inner">
                          <div className="history-details">
                            <div className="history-detail-block history-detail-block-wide">
                              <h4>{t("applyStrike.reason")}</h4>
                              <p>{item?.reason || "-"}</p>
                              {item?.description || item?.internal_note || item?.admin_note ? (
                                <div className="history-detail-copy">
                                  <strong>{t("applyStrike.restriction.meta.internalNote")}</strong>
                                  <TranslateText text={item?.description || item?.internal_note || item?.admin_note} fallback="-" />
                                </div>
                              ) : null}
                            </div>

                            {itemMetaRows.length > 0 ? (
                              <div className="history-detail-grid">
                                {itemMetaRows.map(([label, value]) => (
                                  <div className="history-detail-item" key={label}>
                                    <span>{label}</span>
                                    <strong>{value}</strong>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            <div className="history-detail-actions">
                              {isActive ? (
                                <button type="button" className="strike-history-close-btn restriction-danger-btn" onClick={() => openRestrictionActionModal(item, "close")}>
                                  {t("applyStrike.restriction.history.closeRestriction")}
                                </button>
                              ) : null}
                              {isRestorable ? (
                                <button type="button" className="strike-history-close-btn" onClick={() => openRestrictionActionModal(item, "restore")}>
                                  {t("applyStrike.restriction.history.restoreRestriction")}
                                </button>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <div className="history-show-more">
                {filteredRestrictions.length > HISTORY_BATCH_SIZE ? (
                  visibleRestrictionCount < filteredRestrictions.length ? (
                    <button type="button" onClick={() => setVisibleRestrictionCount((count) => count + HISTORY_BATCH_SIZE)}>
                      {t("ShowMore", "Show 5 more")}
                    </button>
                  ) : (
                    <button type="button" onClick={() => setVisibleRestrictionCount(HISTORY_BATCH_SIZE)}>
                      {t("ShowLess", "Show less")}
                    </button>
                  )
                ) : null}
              </div>
            </>
          )}
        </section>
      ) : null}

      {closeModalOpen ? (
        <CloseStrike
          t={t}
          selectedStrike={selectedStrike}
          getStrikeCategoryLabel={getStrikeCategoryLabel}
          closeStrikeForm={closeStrikeForm}
          setCloseStrikeForm={setCloseStrikeForm}
          closeStrikeErrors={closeStrikeErrors}
          setCloseStrikeErrors={setCloseStrikeErrors}
          closeStrikeLoading={closeStrikeLoading}
          onClose={handleCloseStrikeModal}
          onSubmit={handleCloseStrikeSubmit}
        />
      ) : null}

      {restrictionActionModalType ? (
        <RestrictionActionModal
          t={t}
          type={restrictionActionModalType}
          selectedRestriction={selectedRestriction}
          getRestrictionTypeLabel={getRestrictionTypeLabel}
          reason={restrictionActionReason}
          error={restrictionActionError}
          loading={restrictionActionLoading}
          onReasonChange={handleRestrictionActionReasonChange}
          onClose={handleRestrictionActionModal}
          onSubmit={handleRestrictionActionSubmit}
        />
      ) : null}
    </div>
  );
};

export default UserStrike;
